/**
 * Service for fetching statblocks directly from the new /api/statblocks endpoint
 * This bypasses the CSV transformation and uses the backend's direct Zifusion integration
 */

/**
 * Fetch statblocks directly from the backend
 * @param {string} storeId - Store ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Array<string>} selectedStatblocks - Optional array of statblock types to include
 * @returns {Promise<Object>} Statblocks response
 */
export async function fetchStatblocks(storeId, startDate, endDate, selectedStatblocks = null) {
  try {
    // Build query parameters
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    
    if (selectedStatblocks && selectedStatblocks.length > 0) {
      params.append('selected_statblocks', selectedStatblocks.join(','));
    }
    
    const url = `/api/statblocks/${storeId}?${params.toString()}`;
    
    console.log(`Fetching statblocks from: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include' // Include cookies for authentication
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    
    console.log(`Successfully fetched statblocks for store ${storeId}:`, {
      statblockTypes: data.meta?.statblock_types || [],
      recordsProcessed: data.meta?.records_processed || 0,
      dataSource: data.meta?.data_source
    });
    
    return data;
    
  } catch (error) {
    console.error('Error fetching statblocks:', error);
    throw error;
  }
}

/**
 * Transform backend statblocks response to match the format expected by the frontend
 * @param {Object} response - Response from /api/statblocks endpoint
 * @returns {Object} Transformed data for frontend consumption
 */
export function transformStatblocksResponse(response) {
  try {
    if (!response || !response.data) {
      throw new Error('Invalid statblocks response structure');
    }
    
    const { data, meta } = response;
    const { statblocks } = data;
    
    if (!statblocks) {
      console.warn('No statblocks found in response');
      return {
        statblocks: {},
        csvSections: [],
        weatherData: null,
        meta: meta || {}
      };
    }
    
    // Transform statblocks to match frontend expectations
    const transformedStatblocks = {};
    
    // Map backend statblock names to frontend names
    const statblockMapping = {
      'salesStatBlock': 'sales',
      'hourlyStatBlock': 'hourly',
      'employeeStatBlock': 'employees',
      'taskStatBlock': 'tasks',
      'sampleStatBlock': 'samples',
      'timerStatBlock': 'timers',
      'clockInVarianceStatBlock': 'clockInVariance',
      'timesheetStatBlock': 'timesheets',
      'zigyorders': 'orders'
    };
    
    // Transform each statblock
    Object.entries(statblocks).forEach(([backendKey, statblockData]) => {
      const frontendKey = statblockMapping[backendKey] || backendKey;
      transformedStatblocks[frontendKey] = statblockData;
    });
    
    // Create empty CSV sections since we're bypassing CSV processing
    // This maintains compatibility with existing frontend code
    const csvSections = [];
    
    return {
      statblocks: transformedStatblocks,
      csvSections,
      weatherData: null, // Zifusion doesn't provide weather data
      meta: {
        ...meta,
        dataSource: 'statblocks-api',
        transformedAt: new Date().toISOString()
      }
    };
    
  } catch (error) {
    console.error('Error transforming statblocks response:', error);
    throw error;
  }
}

/**
 * Fetch and transform statblocks in one call
 * @param {string} storeId - Store ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Array<string>} selectedStatblocks - Optional array of statblock types to include
 * @returns {Promise<Object>} Transformed statblocks data
 */
export async function fetchAndTransformStatblocks(storeId, startDate, endDate, selectedStatblocks = null) {
  const response = await fetchStatblocks(storeId, startDate, endDate, selectedStatblocks);
  return transformStatblocksResponse(response);
}
